import { motion } from "framer-motion";
import { Keyboard, Waves, Layers } from "lucide-react";
import { SiseraMethodDemo } from "./SoundMappingDemo";

const steps = [
  {
    n: "01",
    icon: Keyboard,
    title: "Input",
    body: "Type any word your student is struggling with.",
  },
  {
    n: "02",
    icon: Waves,
    title: "Isolate",
    body: "Instantly see the word broken into its visual sound patterns, connected to simple words they already know.",
  },
  {
    n: "03",
    icon: Layers,
    title: "Build",
    body: "Use the interactive guide to rebuild the word with your student piece-by-piece until they sound like a pro.",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative w-full px-4 py-20 sm:px-8 sm:py-28"
    >
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-2xl text-center"
        >
          <span className="font-mono-accent text-xs font-semibold uppercase tracking-[0.25em] text-sky-600">
            How It Works
          </span>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Three steps, right in the middle of your lesson.
          </h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <div className="space-y-6">
            {steps.map((step, i) => (
              <motion.div
                key={step.n}
                data-testid={`how-it-works-step-${step.title.toLowerCase()}`}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-start gap-5 rounded-3xl border border-slate-200/70 bg-white/70 p-6 shadow-sm shadow-sky-950/5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-sky-200 hover:shadow-[0_20px_45px_-18px_rgba(2,132,199,0.2)]"
              >
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-600">
                  <step.icon size={22} strokeWidth={2} />
                </div>
                <div>
                  <span className="font-mono-accent text-xs font-semibold uppercase tracking-[0.2em] text-sky-500">
                    Step {step.n}
                  </span>
                  <h3 className="font-display mt-1 text-xl font-bold text-slate-900">
                    {step.title}
                  </h3>
                  <p className="font-body mt-1 text-base leading-relaxed text-slate-500">
                    {step.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <SiseraMethodDemo />
            <p
              data-testid="demo-future-vision-disclaimer"
              className="font-body mx-auto mt-5 max-w-md text-center text-xs italic leading-relaxed text-slate-400"
            >
              Note: This is an interactive preview. The final Sound Ladder web app
              will allow you to type in any English word and receive an accurate,
              editable coaching breakdown instantly that you can work through with
              your students live in your sessions.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
